import HelperFunctions from '../../../../../global/HelperFunctions';
import _ from 'lodash';
import { useGetDocument } from '../../../../hooks/useGetDocument';
import { set } from 'features/app/appSlice';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useGetReimbursements } from 'pages/reimbursements/hooks/useGetReimbursements';
import { useContext, useMemo } from 'react';
import { useHasCategoryAccess } from 'pages/reimbursements/hooks/useHasCategoryAccess';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { RenderedReimbursementProductsAsFunctionInner } from 'pages/documents/misc/_BlockParser/Reimbursement';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { useGetVariant } from 'pages/documents_v2/hooks/useGetVariant';

export function ReimbursementBlock({ block, index, readOnly = false, restrictions }) {
    const document = useGetDocument();
    const dispatch = useDispatch();
    const hasCategoryAccess = useHasCategoryAccess(document.reimbursementCategoryId);

    return (
        <div
            onClick={showEditModal}
            className={cx({
                'uk-background-hover-parent cursor-pointer': !readOnly && hasCategoryAccess === true,
            })}
        >
            <div className="p-4 border-radius uk-background-muted uk-background-primary-extra-light-child uk-text-muted text-center">
                <ReimbursementContent
                    block={block}
                    readOnly={readOnly}
                    hasCategoryAccess={hasCategoryAccess}
                    categoryId={document.reimbursementCategoryId}
                />
            </div>
        </div>
    );

    function showEditModal() {
        if (hasCategoryAccess === false || readOnly || restrictions.canChangePackage === false) {
            return;
        }

        dispatch(
            set({
                key: 'blockEditModal',
                value: {
                    eventKey: 'reimbursementBlockModal',
                    block,
                    blockIndex: index,
                },
            }),
        );
    }
}

function ReimbursementContent({ block, readOnly, hasCategoryAccess, categoryId }) {
    const { t } = useTranslation('documents');

    const latestContentData = readOnly
        ? HelperFunctions.tryParseJSON(block.baseContent)
        : HelperFunctions.tryParseJSON(block.latestContent);

    if (!latestContentData || !latestContentData.code) {
        return <div>{t('document.navbar.main.editor.left.blocks.types.reimbursementBlock.noCode')}</div>;
    }

    return (
        <>
            <div>{latestContentData.code}</div>

            {hasCategoryAccess === true ? (
                <div>
                    <div>
                        <RenderedReimbursementCode code={latestContentData.code} categoryId={categoryId} />
                    </div>
                    <div>
                        <RenderedProducts block={block} blockData={latestContentData} />
                    </div>
                </div>
            ) : (
                <div className="small font-weight-bold text-center">
                    <div className="my-1">
                        <ExclamationTriangleFill size={18} />
                    </div>
                    {t('global:error.noAccessToLinkedCategory')}
                </div>
            )}
        </>
    );
}

export function RenderedReimbursementCode({ block, code, categoryId }) {
    const { reimbursements } = useGetReimbursements(
        `/api/reimbursement-api/api/categories/${categoryId}`,
        _.isEmpty(code),
    );

    const reimbursement = useMemo(() => {
        if (_.isEmpty(code)) {
            return undefined;
        }

        return reimbursements.find((reimbursement) => reimbursement.code === code);
    }, [reimbursements, code]);

    if (!reimbursement) {
        return null;
    }

    return <>{reimbursement.name}</>;
}

function RenderedProducts({ blockData, block }) {
    const document = useGetDocument();
    const { activeVariantId } = useContext(AreaContext);
    const activeVariant = useGetVariant(activeVariantId);

    if (!activeVariant || activeVariant.parentId === null) {
        return null;
    }

    return (
        <div className="pt-4">
            <RenderedReimbursementProductsAsFunctionInner
                blockData={blockData}
                blockId={block.key}
                document={document}
                activeVariantId={activeVariantId}
            />
        </div>
    );
}
